
import * as React from 'react';
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserDocumentEntity from 'Models/Security/Acl/UserDocumentEntity';
import VisitorsDocumentEntity from 'Models/Security/Acl/VisitorsDocumentEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { FileListPreview } from 'Views/Components/CRUD/Attributes/AttributeFile';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';

import { store } from 'Models/Store';
import { gql } from '@apollo/client';

export interface IDocumentEntityAttributes extends IModelAttributes {
	fileId: string;
	file: Blob;
	name: string;


}

@entity('DocumentEntity', 'Document')
export default class DocumentEntity extends Model
	implements IDocumentEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserDocumentEntity(),
		new VisitorsDocumentEntity(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<DocumentEntity, string>({ file: 'file' })
	@CRUD({
		name: 'File',
		displayType: 'file',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseUuid,
		fileAttribute: 'file',
		displayFunction: attr => attr
			? <FileListPreview url={attr} />
			: 'No File Attached',
	})
	public fileId: string;

	@observable
	public file: Blob;
	

	
	/**
	 * Name
	 */
	@Validators.Required()
	@Validators.Unique()
	@observable
	@attribute<DocumentEntity, string>()
	@CRUD({
		name: 'Name',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public name: string;


	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IDocumentEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IDocumentEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.file !== undefined) {
				this.file = attributes.file;
			}
			if (attributes.fileId !== undefined) {
				this.fileId = attributes.fileId;
			}
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
				contentType: 'multipart/form-data',
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}

	public static getDocument = async (documentName: string) => {
		try {
			const { data } = await store.apolloClient.query({
				query: gql`
					query fetchDocument($documentName: String) {
						documentEntitys(
							where: [{path: "name", comparison: equal, value: [$documentName]}],
							take: 1
						) {
							id
							name
							fileId
							modified
						}
					}
				`,
				variables: {
					documentName,
				},
				fetchPolicy: 'no-cache',
			});
			if (!data.documentEntitys[0]) {
				return null;
			}

			return new DocumentEntity(data.documentEntitys[0]);
		} catch (e) {
			return null;
		}
	}

}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(DocumentEntity.prototype, 'created');
CRUD(modifiedAttr)(DocumentEntity.prototype, 'modified');
